import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";

const togglePublished = async ({
	id,
	published
}: {
	id: number;
	published: boolean;
}) => {
	const response = await cmsClient.patch<IKeyShout>(
		`/homepage/admin/keyshout/${id}/`,
		{ published }
	);

	return response.data;
};

const useToggleKeyShoutPublished = () => {
	const queryClient = useQueryClient();

	const onMutate = async (variables: { id: number; published: boolean }) => {
		await queryClient.cancelQueries(["key-shouts"]);

		const previousActive = queryClient.getQueryData<KeyShout[]>([
			"key-shouts",
			true
		]);
		const previousDraft = queryClient.getQueryData<KeyShout[]>([
			"key-shouts",
			false
		]);

		const sourceKey = variables.published
			? ["key-shouts", false]
			: ["key-shouts", true];
		const targetKey = variables.published
			? ["key-shouts", true]
			: ["key-shouts", false];

		const sourceData = queryClient.getQueryData<KeyShout[]>(sourceKey) ?? [];
		const targetData = queryClient.getQueryData<KeyShout[]>(targetKey) ?? [];

		const item = sourceData.find((shout) => shout.id === variables.id);

		if (item) {
			queryClient.setQueryData<KeyShout[]>(
				sourceKey,
				sourceData.filter((shout) => shout.id !== variables.id)
			);
			queryClient.setQueryData<KeyShout[]>(targetKey, [
				...targetData,
				{ ...item, published: variables.published }
			]);
		}

		return { previousActive, previousDraft };
	};

	const onSuccess = (_data: IKeyShout, variables: { published: boolean }) => {
		const message = variables.published
			? "Key shout is now live"
			: "Key shout moved to drafts";

		toast.success(<CustomToast type="Success" message={message} />, {
			hideProgressBar: true
		});

		queryClient.invalidateQueries(["key-shouts"]);
	};

	const onError = (
		_error: unknown,
		_variables: { id: number; published: boolean },
		context?: { previousActive?: KeyShout[]; previousDraft?: KeyShout[] }
	) => {
		if (context?.previousActive) {
			queryClient.setQueryData(["key-shouts", true], context.previousActive);
		}
		if (context?.previousDraft) {
			queryClient.setQueryData(["key-shouts", false], context.previousDraft);
		}

		toast.error(
			<CustomToast type="Error" message="Something went wrong, try again" />,
			{ hideProgressBar: true }
		);
	};

	const mutation = useMutation(togglePublished, {
		onMutate,
		onError,
		onSuccess
	});

	return mutation;
};

export default useToggleKeyShoutPublished;
